import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  MessageSquare, Compass, Settings, Eye, 
  ArrowRight, CheckCircle2, Phone, Mail
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';

const HowItWorksPage = () => {
  const { t } = useTranslation();

  const steps = [
    {
      number: t('howItWorksPage.steps.step1.number'),
      icon: MessageSquare,
      title: t('howItWorksPage.steps.step1.title'),
      description: t('howItWorksPage.steps.step1.description'),
      details: t('howItWorksPage.steps.step1.details', { returnObjects: true })
    },
    {
      number: t('howItWorksPage.steps.step2.number'),
      icon: Compass,
      title: t('howItWorksPage.steps.step2.title'),
      description: t('howItWorksPage.steps.step2.description'),
      details: t('howItWorksPage.steps.step2.details', { returnObjects: true })
    },
    {
      number: t('howItWorksPage.steps.step3.number'),
      icon: Settings,
      title: t('howItWorksPage.steps.step3.title'),
      description: t('howItWorksPage.steps.step3.description'),
      details: t('howItWorksPage.steps.step3.details', { returnObjects: true })
    },
    {
      number: t('howItWorksPage.steps.step4.number'),
      icon: Eye,
      title: t('howItWorksPage.steps.step4.title'),
      description: t('howItWorksPage.steps.step4.description'),
      details: t('howItWorksPage.steps.step4.details', { returnObjects: true })
    }
  ];

  return (
    <div className="min-h-screen bg-offwhite" data-testid="how-it-works-page">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-navy-900 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 50 Q 25 25, 50 50 T 100 50' stroke='%23C7A24E' stroke-width='0.5' fill='none'/%3E%3C/svg%3E")`,
            backgroundSize: '200px 100px'
          }} />
        </div>
        
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-3xl mx-auto"
          >
            <span className="inline-block text-gold font-space text-sm tracking-widest uppercase pb-2 border-b-2 border-gold/60 mb-6">
              {t('howItWorksPage.badge')}
            </span>
            <h1 className="font-space text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              {t('howItWorksPage.title')}
            </h1>
            <p className="text-platinum-light text-lg md:text-xl leading-relaxed">
              {t('howItWorksPage.subtitle')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Steps Section */}
      <section className="py-20 md:py-28 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            {steps.map((step, index) => (
              <motion.div
                key={step.number}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className={`relative ${index !== steps.length - 1 ? 'pb-16' : ''}`}
              >
                {/* Connector line */}
                {index !== steps.length - 1 && (
                  <div className="absolute left-[27px] top-14 bottom-0 w-0.5 bg-gradient-to-b from-gold to-platinum-light" />
                )}
                
                <div className="flex gap-6 md:gap-10">
                  {/* Step number and icon */}
                  <div className="flex-shrink-0">
                    <div className="w-14 h-14 rounded-2xl bg-navy-900 flex items-center justify-center relative z-10">
                      <step.icon className="w-6 h-6 text-gold" />
                    </div>
                  </div>
                  
                  {/* Content */}
                  <div className="flex-1 pt-1">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="text-gold font-space font-bold text-sm">{step.number}</span>
                    </div>
                    <h3 className="font-space text-2xl md:text-3xl font-bold text-navy-900 mb-4">
                      {step.title}
                    </h3>
                    <p className="text-slate-600 text-lg leading-relaxed mb-6">
                      {step.description}
                    </p>
                    <ul className="space-y-2">
                      {Array.isArray(step.details) && step.details.map((detail) => (
                        <li key={`${step.number}-${detail.slice(0, 20)}`} className="flex items-start gap-3">
                          <CheckCircle2 className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
                          <span className="text-slate-700">{detail}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Reassurance band */}
      <section className="py-16 bg-navy-800">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <p className="text-xl md:text-2xl text-white font-space font-semibold max-w-2xl mx-auto">
              &ldquo;{t('howItWorksPage.reassurance')}&rdquo;
            </p>
          </motion.div>
        </div>
      </section>

      {/* Process Diagram Section */}
      <section className="py-20 md:py-28 bg-offwhite">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-space text-3xl md:text-4xl font-bold text-navy-900 mb-4">
              {t('howItWorksPage.journeyAtGlance')}
            </h2>
          </motion.div>

          <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-2">
            {steps.map((step, index) => (
              <motion.div
                key={step.number}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15 }}
                className="flex items-center"
              >
                <Card className="bg-white border border-platinum-light hover:border-gold/50 transition-colors w-full md:w-auto">
                  <CardContent className="p-6 text-center">
                    <div className="w-12 h-12 rounded-full bg-gold/20 flex items-center justify-center mx-auto mb-3">
                      <step.icon className="w-6 h-6 text-gold" />
                    </div>
                    <p className="text-navy-900 font-semibold text-sm">{step.title}</p>
                  </CardContent>
                </Card>
                {index !== steps.length - 1 && (
                  <ArrowRight className="w-6 h-6 text-gold mx-2 hidden md:block" />
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-28 bg-navy-900">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-space text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
              {t('howItWorksPage.readyToStart')}
            </h2>
            <p className="text-xl text-platinum-light max-w-2xl mx-auto mb-10">
              {t('howItWorksPage.readySubtitle')}
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/quote">
                <Button className="bg-gradient-to-r from-gold-light via-gold to-gold-dark text-navy-900 hover:shadow-xl hover:shadow-gold/25 px-10 py-6 text-lg font-semibold rounded-lg transition-all duration-300">
                  {t('howItWorksPage.getQuote')}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button variant="outline" className="border-2 border-platinum/30 text-white hover:bg-white/10 px-10 py-6 text-lg font-semibold rounded-lg">
                  {t('howItWorksPage.contactUs')}
                </Button>
              </Link>
            </div>

            <div className="flex items-center justify-center gap-6 mt-8 text-platinum-light">
              <a href="tel:+31625381824" className="flex items-center gap-2 hover:text-gold transition-colors">
                <Phone className="w-5 h-5" />
                <span>+31 62 538 1824</span>
              </a>
              <a href="mailto:info@elementarycargo.com" className="flex items-center gap-2 hover:text-gold transition-colors">
                <Mail className="w-5 h-5" />
                <span>info@elementarycargo.com</span>
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default HowItWorksPage;
